import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Users,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Target,
  BarChart3,
  CheckCircle,
  AlertCircle,
  Clock,
  Eye,
  Filter
} from 'lucide-react'
import {
  FilterState
} from '@/types'
import { PremiumTable } from '@/components/enhanced/PremiumTable'
import { getConfidenceLevel } from '@/lib/lead-matching'
import {
  useJobberRequests,
  useTodayRequestsByCity,
  useFranchiseAnalytics,
  useCityAnalytics,
  useWebsiteLeads,
  useLeadMatches,
  useTodayLeadsByCity,
  useJobberMetrics
} from '@/features/jobber/hooks'
import type {
  LeadMatch,
  PaginationState
} from '@/features/jobber/types'
import { formatRelativeDate } from '@/utils/displayFormat'
import { formatPhoneNumber } from '@/features/integrations/utils/integrationUtils'

// Enhanced interfaces for pagination and analytics
interface StatsCardProps {
  title: string
  value: string | number
  subtitle?: string
  trend?: 'up' | 'down' | 'neutral'
  trendValue?: string
  icon: React.ReactNode
  className?: string
}

function StatsCard({ title, value, subtitle, trend, trendValue, icon, className = '' }: StatsCardProps) {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-chart-5" />
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-destructive" />
    return null
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <div className="flex items-center text-xs text-muted-foreground mt-1">
            {getTrendIcon()}
            <span className={trend === 'up' ? 'text-chart-5' : trend === 'down' ? 'text-destructive' : ''}>
              {trendValue && `${trendValue} `}{subtitle}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface ConfidenceBadgeProps {
  score: number
}

function ConfidenceBadge({ score }: ConfidenceBadgeProps) {
  const confidence = getConfidenceLevel(score)

  const colors = {
    high: 'bg-chart-5/10 text-chart-5',
    medium: 'bg-accent/10 text-accent',
    low: 'bg-chart-3/10 text-chart-3',
    none: 'bg-muted text-foreground'
  }

  const icons = {
    high: <CheckCircle className="w-3 h-3 mr-1" />,
    medium: <AlertCircle className="w-3 h-3 mr-1" />,
    low: <Clock className="w-3 h-3 mr-1" />,
    none: <Clock className="w-3 h-3 mr-1" />
  }

  return (
    <Badge className={colors[confidence]}>
      {icons[confidence]}
      {confidence.charAt(0).toUpperCase() + confidence.slice(1)} ({score}%)
    </Badge>
  )
}

interface ChannelCardProps {
  name: string
  metrics: {
    leads: number
    requests: number
    jobs: number
    revenue: number
  }
}

function ChannelCard({ name, metrics }: ChannelCardProps) {
  const conversionRate = metrics.requests > 0 ? ((metrics.jobs / metrics.requests) * 100).toFixed(1) : '0'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Leads</span>
          <span className="font-medium">{metrics.leads}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Requests</span>
          <span className="font-medium">{metrics.requests}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Jobs</span>
          <span className="font-medium">{metrics.jobs}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Revenue</span>
          <span className="font-medium">${metrics.revenue.toLocaleString()}</span>
        </div>
        <div className="pt-2 border-t">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Conversion</span>
            <span className="font-medium text-chart-5">{conversionRate}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function LeadAttributionDashboard() {
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    franchise: undefined,
    channel: undefined,
    dateRange: undefined
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [, setSelectedMatch] = useState<LeadMatch | null>(null)
  const [analyticsSearch, setAnalyticsSearch] = useState('')
  const [activeTab, setActiveTab] = useState('requests')
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  // Pagination states
  const [requestsPagination, setRequestsPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0
  })

  const [leadsPagination, setLeadsPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0
  })

  // Fetch dashboard metrics
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useJobberMetrics(filters)

  // Fetch Jobber requests with pagination
  const {
    data: requestsData,
    isLoading: requestsLoading,
    refetch: refetchRequests
  } = useJobberRequests({
    filters,
    pagination: requestsPagination
  })

  // Update pagination total when data changes
  useEffect(() => {
    if (requestsData?.total !== undefined) {
      setRequestsPagination(prev => ({ ...prev, total: requestsData.total }))
    }
  }, [requestsData?.total])

  // Fetch website leads with pagination
  const { data: leadsData, isLoading: leadsLoading, refetch: refetchLeads } = useWebsiteLeads(filters, leadsPagination)

  // Update pagination total when data changes
  useEffect(() => {
    if (leadsData?.total !== undefined) {
      setLeadsPagination(prev => ({ ...prev, total: leadsData.total }))
    }
  }, [leadsData?.total])

  // Fetch lead matches
  const { data: matches = [], refetch: refetchMatches } = useLeadMatches(filters, searchTerm)

  // Fetch analytics data - LAZY LOADED only when tab is active
  // This prevents 10 simultaneous queries on page load
  const { data: franchiseAnalytics = [], isLoading: franchiseAnalyticsLoading } = useFranchiseAnalytics(
    filters,
    { enabled: activeTab === 'analytics' }
  )

  const { data: cityAnalytics = [], isLoading: cityAnalyticsLoading } = useCityAnalytics(
    filters,
    { enabled: activeTab === 'analytics' }
  )
  void cityAnalytics
  void cityAnalyticsLoading

  // Extract data from paginated responses
  const requests = requestsData ? requestsData.data : []
  const leads = leadsData ? leadsData.data : []

  // Today's leads by city
  const { data: todayHamilton } = useTodayLeadsByCity('Hamilton')
  const { data: todayNorthYork } = useTodayLeadsByCity('North York')

  // Today's Jobber requests by city (created date)
  const {
    data: todayHamiltonRequests,
    refetch: refetchHamiltonRequestsToday
  } = useTodayRequestsByCity('Hamilton')
  const {
    data: todayNorthYorkRequests,
    refetch: refetchNorthYorkRequestsToday
  } = useTodayRequestsByCity('North York')

  const handleRefresh = () => {
    void refetchMetrics()
    void refetchRequests()
    void refetchLeads()
    void refetchMatches()
    void refetchHamiltonRequestsToday()
    void refetchNorthYorkRequestsToday()
  }

  const filteredMatches = matches.filter(match => {
    if (!searchTerm) return true

    const searchLower = searchTerm.toLowerCase()
    return (
      match.leads?.client_name?.toLowerCase().includes(searchLower) ||
      match.leads?.client_email?.toLowerCase().includes(searchLower) ||
      match.leads?.client_phone?.includes(searchTerm) ||
      match.jobber_request_id.toLowerCase().includes(searchLower)
    )
  })

  // Pagination helpers
  const handleRequestsPageChange = (page: number) => {
    setRequestsPagination(prev => ({ ...prev, page }))
  }

  const handleLeadsPageChange = (page: number) => {
    setLeadsPagination(prev => ({ ...prev, page }))
  }

  if (metricsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between pb-6 border-b">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Lead Attribution</h1>
          <p className="text-muted-foreground mt-1">
            Track marketing leads through to Jobber requests and job conversions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} className="shadow-sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Leads"
          value={metrics?.summary.totalLeads || 0}
          subtitle="from marketing campaigns"
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />

        <StatsCard
          title="Jobber Requests"
          value={metrics?.summary.totalRequests || 0}
          subtitle="service requests"
          icon={<Phone className="h-4 w-4 text-muted-foreground" />}
        />

        <StatsCard
          title="Match Rate"
          value={metrics?.summary.matchRate || '0%'}
          subtitle="leads to requests"
          icon={<Target className="h-4 w-4 text-muted-foreground" />}
        />

        <StatsCard
          title="Conversion Rate"
          value={metrics?.summary.conversionRate || '0%'}
          subtitle="requests to jobs"
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      {/* Daily Requests (created today) by City */}
      <div className="grid gap-4 md:grid-cols-2">
        <StatsCard
          title="Requests Today – Hamilton"
          value={todayHamiltonRequests?.total ?? 0}
          subtitle={`${Object.entries(todayHamiltonRequests?.byStatus || {})
            .map(([k, v]) => `${v} ${k}`)
            .join(' • ') || 'No requests today'}`}
          icon={<MapPin className="h-4 w-4 text-chart-4" />}
        />
        <StatsCard
          title="Requests Today – North York"
          value={todayNorthYorkRequests?.total ?? 0}
          subtitle={`${Object.entries(todayNorthYorkRequests?.byStatus || {})
            .map(([k, v]) => `${v} ${k}`)
            .join(' • ') || 'No requests today'}`}
          icon={<MapPin className="h-4 w-4 text-chart-3" />}
        />
      </div>

      {/* Daily Regional Leads */}
      <div className="grid gap-4 md:grid-cols-2">
        <StatsCard
          title="Leads Today – Hamilton"
          value={todayHamilton?.total ?? 0}
          subtitle={`${todayHamilton?.byStatus.new ?? 0} new • ${todayHamilton?.byStatus.contacted ?? 0} contacted • ${todayHamilton?.byStatus.qualified ?? 0} qualified • ${todayHamilton?.byStatus.quoted ?? 0} quoted • ${todayHamilton?.byStatus.won ?? 0} won • ${todayHamilton?.byStatus.lost ?? 0} lost`}
          icon={<MapPin className="h-4 w-4 text-chart-4" />}
        />
        <StatsCard
          title="Leads Today – North York"
          value={todayNorthYork?.total ?? 0}
          subtitle={`${todayNorthYork?.byStatus.new ?? 0} new • ${todayNorthYork?.byStatus.contacted ?? 0} contacted • ${todayNorthYork?.byStatus.qualified ?? 0} qualified • ${todayNorthYork?.byStatus.quoted ?? 0} quoted • ${todayNorthYork?.byStatus.won ?? 0} won • ${todayNorthYork?.byStatus.lost ?? 0} lost`}
          icon={<MapPin className="h-4 w-4 text-chart-3" />}
        />
      </div>

      {/* Enhanced Filters */}
      <Card>
        <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Advanced Filters & Search</CardTitle>
              <CardDescription>
                Filter and drill down into your lead attribution data
              </CardDescription>
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm" className="w-full md:w-auto">
                <Filter className="w-4 h-4 mr-2" />
                {filtersExpanded ? 'Hide advanced filters' : 'Show advanced filters'}
              </Button>
            </CollapsibleTrigger>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Row 1: Search and Basic Filters */}
              <div className="flex flex-col gap-4 md:flex-row md:items-center">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search leads and requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value as FilterState['status'] }))}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Match Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Matches</SelectItem>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="unmatched">Unmatched</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.franchise || 'all'}
                  onValueChange={(value: string) => setFilters(prev => ({
                    ...prev,
                    franchise: value === 'all' ? undefined : (value as 'hamilton' | 'northyork')
                  }))}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Franchise" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Franchises</SelectItem>
                    <SelectItem value="hamilton">Hamilton</SelectItem>
                    <SelectItem value="northyork">North York</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Row 2: Advanced Filters */}
              <CollapsibleContent>
                <div className="flex flex-col gap-4 md:flex-row md:items-center">
                  <Select
                    value={filters.requestStatus || 'all'}
                    onValueChange={(value) => setFilters(prev => ({
                      ...prev,
                      requestStatus: value === 'all' ? undefined : (value as FilterState['requestStatus'])
                    }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Request Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="assessment_completed">Assessment Done</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.timeFrame || 'all'}
                    onValueChange={(value) => setFilters(prev => ({
                      ...prev,
                      timeFrame: value === 'all' ? undefined : (value as FilterState['timeFrame'])
                    }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Time Frame" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="last7days">Last 7 Days</SelectItem>
                      <SelectItem value="last30days">Last 30 Days</SelectItem>
                      <SelectItem value="last90days">Last 90 Days</SelectItem>
                      <SelectItem value="thisyear">This Year</SelectItem>
                    </SelectContent>
                  </Select>

                  <Input
                    placeholder="Filter by city..."
                    value={filters.city || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value || undefined }))}
                    className="w-48"
                  />

                  <Input
                    placeholder="Filter by source..."
                    value={filters.source || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value || undefined }))}
                    className="w-48"
                  />
                </div>
              </CollapsibleContent>

              {/* Active Filters Display */}
              {(filters.requestStatus || filters.timeFrame || filters.city || filters.source || filters.drillDown) && (
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <span className="text-sm text-muted-foreground">Active filters:</span>
                  {filters.drillDown && (
                    <Badge variant="secondary" className="gap-1">
                      <BarChart3 className="w-3 h-3" />
                      Drill-down: {filters.drillDown.type} = {filters.drillDown.value}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 ml-1"
                        onClick={() => setFilters(prev => ({ ...prev, drillDown: undefined }))}
                      >
                        ×
                      </Button>
                    </Badge>
                  )}
                  {filters.requestStatus && filters.requestStatus !== 'all' && (
                    <Badge variant="outline">Status: {filters.requestStatus}</Badge>
                  )}
                  {filters.timeFrame && filters.timeFrame !== 'all' && (
                    <Badge variant="outline">Time: {filters.timeFrame}</Badge>
                  )}
                  {filters.city && (
                    <Badge variant="outline">City: {filters.city}</Badge>
                  )}
                  {filters.source && (
                    <Badge variant="outline">Source: {filters.source}</Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters({
                      status: 'all',
                      franchise: undefined,
                      channel: undefined,
                      dateRange: undefined
                    })}
                    className="h-6 text-xs"
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Collapsible>
      </Card>

      <Tabs defaultValue="requests" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
          <TabsTrigger value="requests">Requests ({requestsPagination.total})</TabsTrigger>
          <TabsTrigger value="leads">Leads ({leadsPagination.total})</TabsTrigger>
          <TabsTrigger value="matches">Matches ({matches.length})</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          <PremiumTable
            title="Jobber Service Requests"
            description={`Showing ${requests.length} of ${requestsPagination.total.toLocaleString()} service requests from Jobber CRM`}
            data={requests}
            columns={[
              { header: 'Request ID', cell: (item: any) => <span className="font-mono text-xs">{item.jobber_request_id}</span> },
              { header: 'Client Name', cell: (item: any) => <span className="font-medium">{item.client_name || 'Unknown'}</span> },
              {
                header: 'Contact',
                className: 'hidden md:table-cell',
                cell: (item: any) => (
                  <div className="space-y-1">
                    {item.client_email && (
                      <div className="text-xs flex items-center gap-1 text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        {item.client_email}
                      </div>
                    )}
                    {item.client_phone && (
                      <div className="text-xs flex items-center gap-1 text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        {formatPhoneNumber(item.client_phone)}
                      </div>
                    )}
                  </div>
                )
              },
              { header: 'Service Type', className: 'hidden lg:table-cell', cell: (item: any) => item.request_title || 'Service Request' },
              { header: 'Franchise', cell: (item: any) => <Badge variant="outline" className="text-xs">{item.franchise || 'Unknown'}</Badge> },
              {
                header: 'Created',
                cell: (item: any) => {
                  const dateParts = formatRelativeDate(item.jobber_created_at)
                  return (
                    <div className="text-sm leading-tight">
                      {dateParts.relative && <div className="font-medium">{dateParts.relative}</div>}
                      <div className="text-xs text-muted-foreground">{dateParts.absolute}</div>
                    </div>
                  )
                }
              },
              {
                header: 'Status',
                cell: (item: any) => (
                  <Badge
                    variant={item.status === 'completed' ? 'default' :
                      item.status === 'in_progress' ? 'outline' : 'secondary'}
                    className="text-xs"
                  >
                    {item.status || 'pending'}
                  </Badge>
                )
              },
              { header: 'Value', className: 'hidden md:table-cell', cell: () => 'TBD' }
            ]}
            isLoading={requestsLoading}
            pagination={{
              currentPage: requestsPagination.page,
              totalPages: Math.ceil(requestsPagination.total / requestsPagination.pageSize),
              onPageChange: handleRequestsPageChange
            }}
            emptyMessage="No service requests found matching your filters."
          />
        </TabsContent>

        <TabsContent value="leads" className="space-y-4">
          <PremiumTable
            title="Website Lead Submissions"
            description={`Showing ${leads.length} of ${leadsPagination.total.toLocaleString()} lead submissions from website forms`}
            data={leads}
            columns={[
              { header: 'Lead ID', cell: (item: any) => <span className="font-mono text-xs">{item.id}</span> },
              { header: 'Client Name', cell: (item: any) => <span className="font-medium">{item.client_name || 'Unknown'}</span> },
              {
                header: 'Contact',
                className: 'hidden md:table-cell',
                cell: (item: any) => (
                  <div className="space-y-1">
                    {item.client_email && (
                      <div className="text-xs flex items-center gap-1 text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        {item.client_email}
                      </div>
                    )}
                    {item.client_phone && (
                      <div className="text-xs flex items-center gap-1 text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        {formatPhoneNumber(item.client_phone)}
                      </div>
                    )}
                  </div>
                )
              },
              {
                header: 'Location',
                className: 'hidden lg:table-cell',
                cell: (item: any) => item.service_street || 'Not provided'
              },
              {
                header: 'Source',
                cell: (item: any) => (
                  <Badge variant="outline" className="text-xs">
                    {item.submission_url ? 'Website Form' : 'Direct'}
                  </Badge>
                )
              },
              {
                header: 'Service Interest',
                className: 'hidden lg:table-cell',
                cell: (item: any) => (
                  <span className="text-xs">
                    {item.service_requested || item.message?.substring(0, 50) + '...' || 'General Inquiry'}
                  </span>
                )
              },
              {
                header: 'Submitted',
                cell: (item: any) => {
                  const dateParts = formatRelativeDate(item.created_at)
                  return (
                    <div className="text-sm leading-tight">
                      {dateParts.relative && <div className="font-medium">{dateParts.relative}</div>}
                      <div className="text-xs text-muted-foreground">{dateParts.absolute}</div>
                    </div>
                  )
                }
              },
              {
                header: 'Match Status',
                cell: () => <Badge variant="secondary" className="text-xs">Unmatched</Badge>
              }
            ]}
            isLoading={leadsLoading}
            pagination={{
              currentPage: leadsPagination.page,
              totalPages: Math.ceil(leadsPagination.total / leadsPagination.pageSize),
              onPageChange: handleLeadsPageChange
            }}
            emptyMessage="No leads found matching your filters."
          />
        </TabsContent>

        <TabsContent value="matches" className="space-y-4">
          <PremiumTable
            title="Lead Attribution Results"
            description={`${filteredMatches.length} matches found`}
            data={filteredMatches}
            columns={[
              {
                header: 'Lead Info',
                cell: (item: any) => (
                  <div className="space-y-1">
                    <div className="font-medium">{item.leads?.client_name || 'Unknown'}</div>
                      <div className="hidden md:flex text-xs text-muted-foreground items-center gap-2">
                        {item.leads?.client_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {item.leads.client_email}
                          </span>
                        )}
                        {item.leads?.client_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {formatPhoneNumber(item.leads.client_phone)}
                          </span>
                        )}
                      </div>
                    </div>
                )
              },
              { header: 'Request ID', cell: (item: any) => <span className="font-mono text-xs">{item.jobber_request_id}</span> },
              { header: 'Match Score', cell: (item: any) => <ConfidenceBadge score={item.match_score || 0} /> },
              { header: 'Franchise', cell: (item: any) => <Badge variant="outline" className="text-xs">{item.franchise === 'hamilton' ? 'Hamilton' : 'North York'}</Badge> },
              { header: 'Job Value', className: 'hidden md:table-cell', cell: (item: any) => <span className="text-xs">{item.job_total ? `$${item.job_total.toLocaleString()}` : '—'}</span> },
              {
                header: 'Status',
                cell: (item: any) => (
                  <Badge
                    className={`text-xs ${item.match_type === 'automatic' ? 'bg-success/10 text-success' :
                      item.match_type === 'manual' ? 'bg-info/10 text-info' :
                        'bg-muted text-foreground'
                      }`}
                  >
                    {item.match_type}
                  </Badge>
                )
              },
              {
                header: 'Actions',
                cell: (item: any) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedMatch(item)}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                )
              }
            ]}
            emptyMessage="No matches found."
          />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(metrics?.byChannel || {}).map(([channel, channelMetrics]) => (
              <ChannelCard key={channel} name={channel} metrics={channelMetrics} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <PremiumTable
            title="Geographic Performance"
            description="Lead performance by postal Forward Sortation Area (FSA)"
            data={Object.entries(metrics?.byLocation || {}).map(([fsa, locationMetrics]) => ({
              fsa,
              ...locationMetrics
            }))}
            columns={[
              {
                header: 'Postal FSA',
                cell: (item: any) => (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="font-mono">{item.fsa}</span>
                  </div>
                )
              },
              { header: 'Leads', cell: (item: any) => item.leads },
              { header: 'Requests', cell: (item: any) => item.requests },
              { header: 'Jobs', cell: (item: any) => item.jobs },
              {
                header: 'Conversion Rate',
                cell: (item: any) => {
                  const rate = item.requests > 0
                    ? ((item.jobs / item.requests) * 100).toFixed(1)
                    : '0'
                  return <span className="font-medium text-success">{rate}%</span>
                }
              }
            ]}
            emptyMessage="No location data available."
          />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Franchise Performance Analytics</CardTitle>
              <CardDescription>
                Detailed analytics breakdown by franchise with time-based trends
              </CardDescription>
              <div className="flex items-center gap-2 pt-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search franchises..."
                    value={analyticsSearch}
                    onChange={(e) => setAnalyticsSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAnalyticsSearch('')}
                  disabled={!analyticsSearch}
                >
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {franchiseAnalyticsLoading ? (
                <div className="space-y-6 animate-pulse">
                  {[...Array(2)].map((_, i) => (
                    <Card key={i} className="p-4">
                      <div className="flex justify-between items-center mb-4">
                        <div className="h-6 bg-muted rounded w-32"></div>
                        <div className="h-6 bg-muted rounded w-24"></div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        {[...Array(4)].map((_, j) => (
                          <div key={j} className="text-center">
                            <div className="h-8 bg-muted rounded w-16 mx-auto mb-2"></div>
                            <div className="h-3 bg-muted rounded w-24 mx-auto"></div>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, j) => (
                          <div key={j}>
                            <div className="h-4 bg-muted rounded w-28 mb-3"></div>
                            <div className="space-y-2">
                              {[...Array(4)].map((_, k) => (
                                <div key={k} className="flex justify-between">
                                  <div className="h-3 bg-muted rounded w-16"></div>
                                  <div className="h-3 bg-muted rounded w-20"></div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {franchiseAnalytics
                    .filter(franchise =>
                      !analyticsSearch ||
                      franchise.franchise.toLowerCase().includes(analyticsSearch.toLowerCase())
                    )
                    .map((franchise) => (
                      <Card key={franchise.franchise} className="p-4 cursor-pointer hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold">{franchise.franchise.toUpperCase()}</h3>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setFilters(prev => ({
                                ...prev,
                                franchise: franchise.franchise as 'hamilton' | 'northyork',
                                drillDown: {
                                  type: 'franchise',
                                  value: franchise.franchise,
                                  parentFilters: { ...prev }
                                }
                              }))}
                              className="h-6 w-6 p-0"
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          </div>
                          <Badge variant="outline">{franchise.totalRequests} requests</Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div className="text-2xl font-bold">{franchise.totalRequests}</div>
                              {(() => {
                                const weekly = franchise.timeAnalytics.weekly;
                                if (weekly.length >= 2) {
                                  const current = weekly[weekly.length - 1]?.count || 0;
                                  const previous = weekly[weekly.length - 2]?.count || 0;
                                  const diff = current - previous;
                                  if (diff > 0) {
                                    return (
                                      <span className="flex items-center text-xs text-success transition-colors hover:text-success">
                                        <TrendingUp className="h-3 w-3" />
                                        <span className="ml-0.5">+{diff}</span>
                                      </span>
                                    );
                                  } else if (diff < 0) {
                                    return (
                                      <span className="flex items-center text-xs text-rose-500 transition-colors hover:text-rose-600">
                                        <TrendingDown className="h-3 w-3" />
                                        <span className="ml-0.5">{diff}</span>
                                      </span>
                                    );
                                  }
                                }
                                return null;
                              })()}
                            </div>
                            <div className="text-sm text-muted-foreground">Total Requests</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-chart-5">
                              ${franchise.totalValue.toLocaleString()}
                            </div>
                            <div className="text-sm text-muted-foreground">Total Value</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold">
                              ${Math.round(franchise.avgValue).toLocaleString()}
                            </div>
                            <div className="text-sm text-muted-foreground">Avg Value</div>
                          </div>
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div className="text-2xl font-bold text-chart-4">
                                {franchise.conversionRate.toFixed(1)}%
                              </div>
                              {(() => {
                                const weekly = franchise.timeAnalytics.weekly;
                                if (weekly.length >= 2) {
                                  const currentCount = weekly[weekly.length - 1]?.count || 0;
                                  const previousCount = weekly[weekly.length - 2]?.count || 0;
                                  const percentChange = previousCount > 0
                                    ? ((currentCount - previousCount) / previousCount * 100)
                                    : 0;
                                  if (percentChange > 5) {
                                    return (
                                      <span className="flex items-center text-xs text-success transition-colors hover:text-success">
                                        <TrendingUp className="h-3 w-3" />
                                      </span>
                                    );
                                  } else if (percentChange < -5) {
                                    return (
                                      <span className="flex items-center text-xs text-rose-500 transition-colors hover:text-rose-600">
                                        <TrendingDown className="h-3 w-3" />
                                      </span>
                                    );
                                  }
                                }
                                return null;
                              })()}
                            </div>
                            <div className="text-sm text-muted-foreground">Conversion Rate</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h4 className="font-medium mb-2">Weekly Trends</h4>
                            <div className="space-y-1">
                              {franchise.timeAnalytics.weekly.slice(-4).map((week) => (
                                <div key={week.week} className="flex justify-between text-sm">
                                  <span>{week.week}</span>
                                  <span>{week.count} requests</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium mb-2">Monthly Trends</h4>
                            <div className="space-y-1">
                              {franchise.timeAnalytics.monthly.slice(-4).map((month) => (
                                <div key={month.month} className="flex justify-between text-sm">
                                  <span>{month.month}</span>
                                  <span>{month.count} requests</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium mb-2">Yearly Trends</h4>
                            <div className="space-y-1">
                              {franchise.timeAnalytics.yearly.map((year) => (
                                <div key={year.year} className="flex justify-between text-sm">
                                  <span>{year.year}</span>
                                  <span>{year.count} requests</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Time-Based Analytics</CardTitle>
              <CardDescription>
                Request volume and trends over time periods
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Weekly Performance</h3>
                  <div className="space-y-2">
                    {franchiseAnalytics[0]?.timeAnalytics.weekly.slice(-8).map((week) => (
                      <div key={week.week} className="flex justify-between items-center">
                        <span className="text-sm">{week.week}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{week.count}</span>
                          <div
                            className="h-2 bg-chart-4 rounded"
                            style={{
                              width: `${(week.count / Math.max(...(franchiseAnalytics[0]?.timeAnalytics.weekly.map(w => w.count) || [1]))) * 60}px`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Monthly Performance</h3>
                  <div className="space-y-2">
                    {franchiseAnalytics[0]?.timeAnalytics.monthly.slice(-6).map((month) => (
                      <div key={month.month} className="flex justify-between items-center">
                        <span className="text-sm">{month.month}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{month.count}</span>
                          <div
                            className="h-2 bg-chart-5 rounded"
                            style={{
                              width: `${(month.count / Math.max(...(franchiseAnalytics[0]?.timeAnalytics.monthly.map(m => m.count) || [1]))) * 60}px`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Yearly Performance</h3>
                  <div className="space-y-2">
                    {franchiseAnalytics[0]?.timeAnalytics.yearly.map((year) => (
                      <div key={year.year} className="flex justify-between items-center">
                        <span className="text-sm">{year.year}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{year.count}</span>
                          <div
                            className="h-2 bg-chart-3 rounded"
                            style={{
                              width: `${(year.count / Math.max(...(franchiseAnalytics[0]?.timeAnalytics.yearly.map(y => y.count) || [1]))) * 60}px`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
